// Copyright (c) 2026 Solartek LLC. All rights reserved.
/**
 * Round-the-clock scheduling (Enterprise, module `advanced_scheduling`; migration 20261007000100, docs/enterprise/plan.md §8–§10):
 * 24/7 rotation templates, coverage targets, additional (double) shift assignments and temporary branch deployments.
 */
import { z } from 'zod';
import { codeSchema, isoDateSchema, paginationQuerySchema, timeSchema, uuidSchema, weeklyOffDaysSchema } from '../common.js';
// ----- additional (double) shift assignments --------------------------------------------------------------------------------
export const additionalShiftAssignmentInputSchema = z.object({
    employeeId: uuidSchema,
    shiftId: uuidSchema,
    effectiveFrom: isoDateSchema,
    /** Inclusive last day; null / absent = open-ended. */
    effectiveTo: isoDateSchema.nullable().optional(),
}).refine((v) => !v.effectiveTo || v.effectiveTo >= v.effectiveFrom, { message: 'The last day cannot be before the first day', path: ['effectiveTo'] });
export const additionalShiftAssignmentUpdateSchema = z.object({ effectiveTo: isoDateSchema.nullable() });
/** An open-ended additional assignment is checked against the employee's shifts over its first this many days. */
export const ADDITIONAL_SHIFT_CHECK_DAYS = 92;
/** Why an additional shift cannot be combined with the day's shift (packages/domain composeDoubleShift), or is refused outright. */
export const ADDITIONAL_SHIFT_REFUSALS = ['SAME_SHIFT', 'NOT_FIXED', 'OVERLAP', 'TOO_LONG', 'INACTIVE'];
export const additionalShiftAssignmentListQuerySchema = paginationQuerySchema.extend({
    employeeId: uuidSchema.optional(),
    shiftId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
    activeOn: isoDateSchema.optional(),
});
// ----- branch deployments ---------------------------------------------------------------------------------------------------
export const branchDeploymentInputSchema = z.object({
    employeeId: uuidSchema,
    branchId: uuidSchema,
    fromDate: isoDateSchema,
    /** Inclusive last day (at most 367 days after the first). */
    toDate: isoDateSchema,
    reason: z.string().trim().min(3).max(1000),
    /**
     * Enrol the employee on the branch's active terminals (async device job) ON THE FIRST DAY (host branch time): at once when
     * the deployment starts today, else by the daily sweep. Removed again after the last day.
     */
    enrolOnDevices: z.boolean().default(true),
}).refine((v) => v.toDate >= v.fromDate, { message: 'The last day cannot be before the first day', path: ['toDate'] });
export const cancelBranchDeploymentSchema = z.object({ reason: z.string().trim().min(3).max(1000) });
export const BRANCH_DEPLOYMENT_STATUSES = ['scheduled', 'active', 'ended', 'cancelled'];
export const branchDeploymentListQuerySchema = paginationQuerySchema.extend({
    employeeId: uuidSchema.optional(),
    /** Host OR home branch. */
    branchId: uuidSchema.optional(),
    status: z.enum(BRANCH_DEPLOYMENT_STATUSES).optional(),
    activeOn: isoDateSchema.optional(),
});
// ----- round-the-clock templates --------------------------------------------------------------------------------------------
/**
 * 24/7 rotation templates (docs/enterprise/plan.md §8). Each crew follows the same cycle, offset so that every shift of every
 * day is covered:
 *   TWO_SHIFT_4ON4OFF  2 × 12 h (day / night), 4 crews: 4 days, 4 off, 4 nights, 4 off (16-day cycle)
 *   TWO_SHIFT_PANAMA_223      2 × 12 h, 4 crews: 2-2-3 (Panama), 14-day cycle per shift kind, 28-day full cycle
 *   THREE_SHIFT_CONTINENTAL   3 × 8 h (morning / evening / night), 4 crews: 2 mornings, 2 evenings, 2 nights, 2 off (8-day cycle)
 *   THREE_SHIFT_WEEKLY        3 × 8 h, 4 crews: a week of mornings, of evenings, of nights, a week off (28-day cycle)
 */
export const ROUND_THE_CLOCK_TEMPLATES = ['TWO_SHIFT_4ON4OFF', 'TWO_SHIFT_PANAMA_223', 'THREE_SHIFT_CONTINENTAL', 'THREE_SHIFT_WEEKLY'];
export const roundTheClockInputSchema = z.object({
    template: z.enum(ROUND_THE_CLOCK_TEMPLATES),
    /** Code prefix of the created shifts and patterns (e.g. "247" → 247-D, 247-N, 247-A…). */
    codePrefix: codeSchema.refine((c) => c.length <= 20, { message: 'At most 20 characters' }),
    namePrefix: z.string().trim().min(1).max(60),
    /** Start of the first shift of the day (the others follow back to back). */
    firstShiftStart: timeSchema.default('06:00'),
    /** Day 0 of every crew's cycle. */
    anchorDate: isoDateSchema,
    /** Unpaid break inside each shift, minutes (0 = none). */
    breakMinutes: z.number().int().min(0).max(120).default(60),
    /** Optional: put existing teams on crews A, B, C, D (TEAM assignments from the anchor date). */
    crewTeams: z.array(z.object({ crew: z.enum(['A', 'B', 'C', 'D']), teamId: uuidSchema })).max(4).default([])
        .refine((a) => new Set(a.map((c) => c.crew)).size === a.length && new Set(a.map((c) => c.teamId)).size === a.length, { message: 'Each crew and team once' }),
    /** Optional: a coverage target per created shift on this branch (min headcount on every weekday). */
    coverage: z.object({ branchId: uuidSchema, minHeadcount: z.number().int().min(1).max(10000) }).nullable().default(null),
});
// ----- coverage --------------------------------------------------------------------------------------------------------------
export const shiftCoverageInputSchema = z.object({
    branchId: uuidSchema,
    shiftId: uuidSchema,
    /** A place of the branch (site, floor, zone…): only the people working there count (docs/locations.md); null = the whole branch. */
    locationId: uuidSchema.nullable().optional(),
    weekdays: weeklyOffDaysSchema.min(1).refine((a) => new Set(a).size === a.length, { message: 'Each weekday once' }).default([0, 1, 2, 3, 4, 5, 6]),
    minHeadcount: z.number().int().min(1).max(10000),
});
export const shiftCoverageUpdateSchema = z.object({
    weekdays: weeklyOffDaysSchema.min(1).refine((a) => new Set(a).size === a.length, { message: 'Each weekday once' }).optional(),
    minHeadcount: z.number().int().min(1).max(10000).optional(),
});
export const shiftCoverageListQuerySchema = z.object({ branchId: uuidSchema.optional(), shiftId: uuidSchema.optional(), locationId: uuidSchema.optional() });
/** Scheduled head count vs the targets per day and shift (rotation patterns, assignments, double shifts; leave and offs excluded). */
/** The coverage report spans at most this many days. */
export const SHIFT_COVERAGE_REPORT_MAX_DAYS = 62;
export const shiftCoverageReportQuerySchema = z.object({ branchId: uuidSchema, from: isoDateSchema, to: isoDateSchema })
    .refine((v) => v.to >= v.from, { message: 'The last day cannot be before the first day', path: ['to'] })
    .refine((v) => (Date.parse(`${v.to}T00:00:00Z`) - Date.parse(`${v.from}T00:00:00Z`)) / 86_400_000 < SHIFT_COVERAGE_REPORT_MAX_DAYS, { message: `At most ${SHIFT_COVERAGE_REPORT_MAX_DAYS} days`, path: ['to'] });
//# sourceMappingURL=enterprise-scheduling.js.map